/* eslint-disable no-unused-vars */
/* eslint-disable no-loop-func */
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import CoalitionCheckbox from "./CoalitionCheckbox";
import { formatString } from "../../utils/common";

ChartJS.register(ArcElement, Tooltip, Legend);

const colors = {
  deigreng: "#39B54A",
  lsap: "#F00035",
  csv: "#FFCC00",
  dp: "#002C4F",
  arleit: "#FF6600",
  deilenk: "#e10736",
  kpl: "#FF0000",
  ebl: "#0FA6A6",
  adr: "#b8def1",
  biergerlest: "#653796",
  schetterbierger: "#653796",
  arequipe: "#8A365D",
  piraten: "#a62a8c",
  piratepartei: "#a62a8c",
  schetter: "#662e88",
  biergerlestgemengcliarref: "#eaca87",
  mirdvollek: "#016fb8",
  fraileschtarleitfirrouspertmompech: "#ff6600",
};

function CoalitionBuilder({ data, currentParty, t }) {
  const [selectedParties, setSelectedParties] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "mandates",
        data: [],
        backgroundColor: [],
      },
    ],
  });
  const chartRef = useRef(null);

  const handlePartySelection = (index) => {
    setSelectedParties((prev) =>
      prev.map((selected, i) => (i === index ? !selected : selected))
    );
  };

  useEffect(() => {
    const highestVotesIndex = data.reduce(
      (highestIndex, currentParty, currentIndex, array) =>
        currentParty.percentage > array[highestIndex].percentage
          ? currentIndex
          : highestIndex,
      0
    );

    setSelectedParties(data.map((_, index) => index === highestVotesIndex));
  }, [data]);

  useEffect(() => {
    const currentPartyIndex = data.findIndex(
      (party) => party.partyName === currentParty
    );

    setSelectedParties(data.map((_, index) => index === currentPartyIndex));
  }, [data, currentParty]);

  const totalMandates = data
    .filter((party) => party.mandates > 0)
    .reduce((sum, party) => {
      if (typeof party.mandates !== "number") {
        console.error(`Mandates for ${party.partyName} is not a number!`);
        return sum;
      }
      return sum + party.mandates;
    }, 0);

  const selectedData = useMemo(() => {
    return data.filter((_, i) => selectedParties[i]);
  }, [data, selectedParties]);

  const totalPercentage = useMemo(
    () => selectedData.reduce((sum, party) => sum + party.percentage, 0),
    [selectedData]
  );

  const remainingPercentage = useMemo(
    () => 100 - totalPercentage,
    [totalPercentage]
  );

  useEffect(() => {
    if (chartRef.current) {
      if (chartRef.current.chartInstance) {
        chartRef.current.chartInstance.destroy(); // Destroy previous instance
      }

      const totalSelectedMandates = selectedData.reduce(
        (sum, party) => sum + party.mandates,
        0
      );

      const remainingMandates = totalMandates - totalSelectedMandates;

      const updatedData = {
        labels: [...selectedData.map(({ partyName }) => partyName), ""],
        datasets: [
          {
            data: [
              ...selectedData.map(({ mandates }) => mandates),
              remainingMandates,
            ],
            backgroundColor: [
              ...selectedData.map(
                ({ partyName }) => colors[formatString(partyName)]
              ),
              "#F4F4F4",
            ],
          },
        ],
      };

      setChartData(updatedData);
    }
  }, [chartRef, selectedData, totalMandates]);

  const options = {
    responsive: true,
    rotation: 270,
    circumference: 180,
    onClick: (_, activeElements) => {
      if (activeElements.length > 0) {
        const index = activeElements[0].index;
        handlePartySelection(index);
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            var label = context.parsed + " Mandates";
            return label;
          },
        },
      },
    },
  };

  const totalSelectedMandates = selectedData.reduce(
    (sum, party) => sum + party.mandates,
    0
  );
  const remainingMandates = totalMandates - totalSelectedMandates;
  const partyWidth = (mandates) => (mandates / totalMandates) * 100;

  return (
    <div className="md:mt-0 mt-10">
      <div className="flex flex-col mb-4">
        <span className="text-lg font-medium leading-6">
          {t("possibleCoaltions")}
        </span>
        <span className="leading-[17px]">{t("lastPollUpdate")}</span>
      </div>

      <div className="flex flex-col justify-between md:flex-row">
        <div
          className="flex flex-col justify-center items-center h-[300px] w-full
      md:w-1/2 pb-2"
        >
          <Doughnut data={chartData} options={options} ref={chartRef} />
          <div className="flex flex-col justify-center mx-4 w-full h-[70px]  md:-mt-12">
            <div className="h-22 relative">
              <div className="absolute top-0 bottom-0 left-0 right-0 flex">
                {selectedData.map((party, index) => (
                  <div
                    key={index}
                    className="flex-grow"
                    style={{
                      width: `${partyWidth(party.mandates)}%`,
                      backgroundColor: colors[formatString(party.partyName)],
                    }}
                  ></div>
                ))}
                <div
                  className="flex-grow bg-wort-background"
                  style={{
                    width: `${
                      ((totalMandates - totalSelectedMandates) /
                        totalMandates) *
                      100
                    }%`,
                  }}
                ></div>
                <div
                  className="absolute left-1/2 w-1 h-14 bg-black transform -translate-y-1/2"
                  style={{ width: 0.8, top: "10px" }}
                ></div>
              </div>
            </div>
            <div className="flex justify-center mt-3">
              <span className="text-xs mt-1">
                {totalMandates / 2} {t("mandates")}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col mt-10 md:w-1/2 w-full md:pl-10">
          {data
            .filter((party) => party.mandates > 0)
            .map((party, index) => {
              return (
                <div key={index} className="flex items-center mb-2">
                  <CoalitionCheckbox
                    checked={selectedParties[index]}
                    onChange={() => handlePartySelection(index)}
                    className="mr-2 h-8 w-8"
                  />
                  <div
                    className="w-29 h-22"
                    style={{
                      backgroundColor: colors[formatString(party.partyName)],
                    }}
                  ></div>
                  <span className="ml-2 text-lg leading-5">
                    {party.partyName}
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default React.memo(CoalitionBuilder);
